import { noop } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { KeyNames } from "@/utils/enums";
import { createKeyHandler } from "@/utils/keyboard";

export type NumericKeyHandler = (
    event: KeyboardEvent | React.KeyboardEvent<Element>
) => void;

export const onNumericKeyDownHandler: NumericKeyHandler = createKeyHandler([
    {
        keys: [
            KeyNames.ARROW_DOWN,
            KeyNames.ARROW_UP,
            KeyNames.ARROW_LEFT,
            KeyNames.ARROW_RIGHT,
            KeyNames.TAB,
            KeyNames.SHIFT,
            KeyNames.CTRL,
            KeyNames.ALT,
            KeyNames.BACKSPACE,
            KeyNames.DELETE,
            KeyNames.HOME,
            KeyNames.END,
            KeyNames.ENTER,
            KeyNames.META
        ],
        handler: noop
    },
    {
        isPressed: (k, event) => {
            const isClipboardShortcut =
                (event.ctrlKey || event.metaKey) && /^(c|x|v)$/i.test(k);

            return !isClipboardShortcut && Number.isNaN(parseInt(k, 10));
        },
        handler: (e) => e.preventDefault()
    }
]);

const onNumericKeyUpHandler: NumericKeyHandler = noop;

export type useNumericFieldProps<T extends FieldValues> = {
    name: Path<T>;
    required: boolean;
    defaultValue?: number;
    min?: number;
    max?: number;
    maxLength?: number;
    onKeyDown?: (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => (cb: NumericKeyHandler) => void;
    onKeyUp?: (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => (cb: NumericKeyHandler) => void;
};

export const toNumeric = (
    value: string | null | undefined,
    required: boolean,
    defaultValue?: number
) => {
    return value === "" || !isDefined(value)
        ? required
            ? (defaultValue ?? 0)
            : (defaultValue ?? undefined)
        : parseInt(value, 10);
};

const DefaultKeyDownEventHandler =
    (e: React.KeyboardEvent<HTMLInputElement>) =>
    (cb: typeof onNumericKeyDownHandler) => {
        cb(e);
    };

const DefaultKeyUpEventHandler =
    (e: React.KeyboardEvent<HTMLInputElement>) =>
    (cb: typeof onNumericKeyUpHandler) => {
        cb(e);
    };

const useNumericField = <T extends FieldValues>({
    name,
    required,
    min,
    max,
    defaultValue,
    maxLength = Number.POSITIVE_INFINITY,
    onKeyDown = DefaultKeyDownEventHandler,
    onKeyUp = DefaultKeyUpEventHandler
}: useNumericFieldProps<T>) => {
    const { setValue } = useFormContext<T>();

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
        return onKeyDown(e)(onNumericKeyDownHandler);
    };

    const onBlurHandler =
        (onBlurCb: () => void) => (e: React.FocusEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const numericValue = toNumeric(value, required, defaultValue);
            let newValue;
            if (
                isDefined(min) &&
                isDefined(numericValue) &&
                numericValue < min
            ) {
                newValue = min;
            }
            if (
                isDefined(max) &&
                isDefined(numericValue) &&
                numericValue > max
            ) {
                newValue = max;
            }
            if (isDefined(newValue)) {
                setValue(name, newValue as PathValue<T, Path<T>>);
            }

            onBlurCb();
        };

    const onChangeHandler =
        (
            oldValue: PathValue<T, Path<T>>,
            onChangeCb: (...event: any[]) => void
        ) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value, validity } = e.target;
            if (!value.match(/^[0-9]*$/)) {
                e.preventDefault();
                return;
            }

            if (!value && !validity.valid) {
                e.preventDefault();
                setValue(name, oldValue);
                return;
            }

            const hasPassedMaxLength =
                maxLength &&
                maxLength > 0 &&
                value.toString().length > maxLength;

            if (hasPassedMaxLength) {
                e.preventDefault();
                return;
            }

            const numericValue = toNumeric(value, required, defaultValue);

            if (isDefined(numericValue)) {
                if (isDefined(min) && numericValue < min) {
                    e.preventDefault();
                    return;
                }
                if (isDefined(max) && numericValue > max) {
                    e.preventDefault();
                    return;
                }
            }

            onChangeCb(e);
        };

    const onPasteHandler = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (e.clipboardData?.getData) {
            setValue(
                name,
                e.clipboardData
                    .getData("text/plain")
                    .replace(/\D/g, "") as PathValue<T, Path<T>>
            );
        }
    };

    const onKeyUpHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
        return onKeyUp(e)(onNumericKeyUpHandler);
    };

    return {
        onKeyDownHandler,
        onBlurHandler,
        onChangeHandler,
        onPasteHandler,
        onKeyUpHandler
    };
};

export { useNumericField };
